import { useEffect, useMemo, useRef, useState } from "react";
import {
  Boxes,
  ChevronDown,
  LockKeyhole,
  LogOut,
  Mail,
  MoveRight,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";
import { Navigate } from "@tanstack/react-router";
import {
  accountRepository,
  fallbackAccountProducts,
  type AccountProduct,
} from "../platform/accountRepository";
import { hasPaperKingdomProductAccess } from "../domain/access";
import { usePortal } from "../state/PortalContext";

const productPresentation: Record<
  string,
  { description: string; logo: string; logoAlt: string }
> = {
  "paper-kingdom": {
    description: "Plan and build coordinated publishing catalogues.",
    logo: "/paperkingdom/Logo.png",
    logoAlt: "Paper Kingdom",
  },
  "shop-theme-ai": {
    description: "Create complete storefront themes with AI.",
    logo: "/account-assets/shoptheme-symbol.png",
    logoAlt: "Shop Theme AI",
  },
};

function fallbackProducts(paperKingdomOwned: boolean) {
  return fallbackAccountProducts.map((product) => ({
    ...product,
    owned: product.productKey === "paper-kingdom" && paperKingdomOwned,
  }));
}

export function AccountPage() {
  const { authStatus, session, entitlements, canAdmin, signOut } = usePortal();
  const [products, setProducts] = useState<AccountProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [accountDetailsOpen, setAccountDetailsOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const accountDetailsRef = useRef<HTMLElement>(null);
  const accountDetailsCloseRef = useRef<HTMLButtonElement>(null);
  const sessionEmail = session?.email ?? "";
  const paperKingdomOwned = hasPaperKingdomProductAccess(entitlements, canAdmin);
  const localFallback = useMemo(
    () => fallbackProducts(paperKingdomOwned),
    [paperKingdomOwned],
  );

  useEffect(() => {
    document.title = "Your products";
  }, []);

  useEffect(() => {
    const closeMenu = (event: PointerEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setAccountMenuOpen(false);
        setAccountDetailsOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeMenu);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  useEffect(() => {
    if (!accountDetailsOpen) return;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    accountDetailsCloseRef.current?.focus();

    const keepFocusInsideDialog = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const focusable = accountDetailsRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", keepFocusInsideDialog);
    return () => {
      document.removeEventListener("keydown", keepFocusInsideDialog);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [accountDetailsOpen]);

  useEffect(() => {
    if (authStatus !== "ready" || !sessionEmail) return;

    let active = true;
    setLoadingProducts(true);
    setLoadError("");

    if (!accountRepository.configured) {
      setProducts(localFallback);
      setLoadingProducts(false);
      return;
    }

    void accountRepository
      .loadProducts(sessionEmail)
      .then((loaded) => {
        if (active) setProducts(loaded);
      })
      .catch(() => {
        if (!active) return;
        setProducts(localFallback);
        setLoadError("Your latest product access could not be loaded. Please refresh and try again.");
      })
      .finally(() => {
        if (active) setLoadingProducts(false);
      });

    return () => {
      active = false;
    };
  }, [authStatus, localFallback, sessionEmail]);

  if (authStatus === "loading") {
    return <main className="account-boot" aria-label="Opening your account" />;
  }

  if (authStatus === "signed-out" || !session) {
    return <Navigate to="/login" replace />;
  }

  const ownedProductCount = products.filter((product) => product.owned).length;
  const accountInitial = session.email.trim().charAt(0).toUpperCase() || "A";

  return (
    <main className="account-page">
      <header className="account-header">
        <div className="account-header__inner">
          <div className="account-header__title">
            <span className="account-header__mark" aria-hidden="true">
              <Boxes size={17} />
            </span>
            <span>Product access</span>
          </div>
          <div className="account-menu" ref={accountMenuRef}>
            <button
              type="button"
              className="account-menu__trigger"
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              aria-label="Open account menu"
              onClick={() => setAccountMenuOpen((open) => !open)}
            >
              <span className="account-avatar">{accountInitial}</span>
              <span>Account</span>
              <ChevronDown size={15} aria-hidden="true" />
            </button>
            {accountMenuOpen ? (
              <div className="account-menu__popover" role="menu">
                {canAdmin ? (
                  <a href="/admin" role="menuitem">
                    <ShieldCheck size={16} aria-hidden="true" />
                    <span>
                      <strong>Access management</strong>
                      <small>Grant or review product access</small>
                    </span>
                  </a>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAccountMenuOpen(false);
                    setAccountDetailsOpen(true);
                  }}
                >
                  <UserRound size={16} aria-hidden="true" />
                  <span>
                    <strong>Account details</strong>
                    <small>View your login and access</small>
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void signOut()}
                >
                  <LogOut size={16} aria-hidden="true" />
                  <span>
                    <strong>Sign out</strong>
                    <small>End this session</small>
                  </span>
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <section className="account-products" aria-labelledby="products-title">
        <div className="account-products__heading">
          <h1 id="products-title">Your products</h1>
          <p>Choose a product to continue.</p>
        </div>

        {loadError ? <p className="account-load-error" role="alert">{loadError}</p> : null}

        <div className="product-card-grid" aria-busy={loadingProducts}>
          {(loadingProducts ? fallbackAccountProducts : products).map((product) => {
            const presentation = productPresentation[product.productKey] ?? {
              description: "Open this product workspace.",
              logo: "",
              logoAlt: product.displayName,
            };

            return (
              <article
                className={`product-access-card ${product.owned ? "is-owned" : "is-locked"}`}
                key={product.productKey}
              >
                <div className={`product-access-card__logo product-access-card__logo--${product.productKey}`}>
                  {presentation.logo ? (
                    <img src={presentation.logo} alt={presentation.logoAlt} />
                  ) : (
                    <strong>{product.displayName}</strong>
                  )}
                </div>
                <div className="product-access-card__copy">
                  <h2>{product.displayName}</h2>
                  <p>{presentation.description}</p>
                </div>
                {loadingProducts ? (
                  <span className="product-access-card__loading">Checking access…</span>
                ) : product.owned ? (
                  <a className="product-access-card__action" href={product.routePath}>
                    Open product
                    <MoveRight size={17} aria-hidden="true" />
                  </a>
                ) : (
                  <span className="product-access-card__locked">
                    <LockKeyhole size={15} aria-hidden="true" />
                    Not purchased
                  </span>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {accountDetailsOpen ? (
        <div
          className="account-details-backdrop"
          role="presentation"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) setAccountDetailsOpen(false);
          }}
        >
          <section
            className="account-details-dialog"
            ref={accountDetailsRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-details-title"
          >
            <header>
              <div className="account-details-dialog__identity">
                <span className="account-avatar account-avatar--large">
                  {accountInitial}
                </span>
                <div>
                  <span>Member account</span>
                  <h2 id="account-details-title">Account details</h2>
                </div>
              </div>
              <button
                type="button"
                ref={accountDetailsCloseRef}
                className="account-details-dialog__close"
                aria-label="Close account details"
                onClick={() => setAccountDetailsOpen(false)}
              >
                <X size={18} />
              </button>
            </header>
            <div className="account-detail-list">
              <div>
                <span className="account-detail-list__icon" aria-hidden="true">
                  <Mail size={17} />
                </span>
                <span>
                  <small>Email address</small>
                  <strong>{session.email}</strong>
                </span>
              </div>
              <div>
                <span className="account-detail-list__icon" aria-hidden="true">
                  <Boxes size={17} />
                </span>
                <span>
                  <small>Product access</small>
                  <strong>
                    {loadingProducts
                      ? "Checking access…"
                      : `${ownedProductCount} product${ownedProductCount === 1 ? "" : "s"} available`}
                  </strong>
                </span>
              </div>
            </div>
            <footer>
              <button
                type="button"
                onClick={() => setAccountDetailsOpen(false)}
              >
                Done
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </main>
  );
}
